import type { SupabaseClient } from '@supabase/supabase-js';
import { awaitJob } from '../../lib/await-job.js';
import { showToast, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';
import { reconcileWithChannel, resolveDefaultSelection, warehouseSummary } from '../../lib/warehouse-selection.js';

// Statische Deko-SVGs aus dem Design-Handoff "Kaufland Einstellungen" (currentColor, stroke).
const SVG_TAGS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></svg>';
const SVG_TRUCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 18V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l3 3v5a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="1.8"/><circle cx="17.5" cy="18.5" r="1.8"/></svg>';
const SVG_CHECK_SM = '<svg class="ck" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6 9 17l-5-5"/></svg>';
const SVG_LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';

// Storefront-Liste ist NICHT mehr hartcodiert — sie kommt katalog-getrieben vom Backend
// (kaufland-storefronts-available → supportedStorefronts, Quelle kaufland.storefronts).
// Neuer Marktplatz = 1 Katalog-Zeile → erscheint hier automatisch beim Abruf.
type StorefrontDef = { code: string; label: string };
// Fallback, falls das Backend (noch) kein supportedStorefronts liefert (alter Stand während Deploy).
const FALLBACK_STOREFRONTS: StorefrontDef[] = [
  { code: 'de', label: 'Deutschland' }, { code: 'cz', label: 'Tschechien' },
  { code: 'sk', label: 'Slowakei' },    { code: 'pl', label: 'Polen' },
  { code: 'at', label: 'Österreich' },  { code: 'fr', label: 'Frankreich' },
  { code: 'it', label: 'Italien' },
];

const CATEGORY_POLL_INTERVAL_MS = 10_000;

interface SettingsCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

interface StorefrontAddResponse {
  ok?: boolean;
  storefront?: string;
  label?: string;
  channelId?: string;
  alreadyPresent?: boolean;
  storefronts?: string[];
  error?: {
    code: string;
    message: string;
    probe?: { ok: boolean; status?: number; message?: string };
  };
}

interface StoredStorefront {
  storefront: string;
  vario_channel_id: string;
  vario_channel_label: string;
  vario_storage_id?: string | null;
  vario_storage_label?: string | null;
  vario_storage_ids?: string[] | null;
  suppress_stock_push?: boolean | null;
  auto_takeover_orders?: boolean | null;
  vario_payment_method_id?: string | null;
  vario_payment_method_label?: string | null;
  vario_payment_term_id?: string | null;
  vario_payment_term_label?: string | null;
  vario_delivery_method_id?: string | null;
  vario_delivery_method_label?: string | null;
  vario_delivery_term_id?: string | null;
  vario_delivery_term_label?: string | null;
}

interface StatusResponse {
  ok?: boolean;
  available?: string[];
  probes?: Array<{ storefront: string; ok: boolean; status?: number; message?: string }>;
  dbStorefronts?: StoredStorefront[];
  supportedStorefronts?: Array<{ code: string; name: string; currency: string }>;
  varioChannels?: Array<{ id: string; label: string }>;
  /** Je Storefront die Lager, die dem Vario-Kanal aktuell zugeordnet sind. DREIWERTIG:
   *  Array = echter Stand aus Vario, `null` = nicht lesbar, Schlüssel fehlt = kein Kanal.
   *  Nachgezogen wird die gespeicherte Auswahl NUR beim Array — bei `null` würde ein
   *  Vario-Aussetzer sonst die Bestandsquelle des Händlers leeren. */
  channelStorageIds?: Record<string, string[] | null>;
  varioError?: string;
  error?: { code: string; message: string };
}

interface VarioWarehouse {
  id: string;
  number: string | null;
  displayName: string | null;
  active: boolean | null;
  salesStorage: boolean | null;
}

interface PaymentOption {
  id: string;
  label: string;
}

let cachedWarehouses: VarioWarehouse[] | null = null;
let cachedPaymentMethods: PaymentOption[] | null = null;
let cachedPaymentTerms: PaymentOption[] | null = null;
let cachedDeliveryMethods: PaymentOption[] | null = null;
let cachedDeliveryTerms: PaymentOption[] | null = null;

interface CategoryStatusRow {
  storefront: string;
  kaufland_count: number;
  vario_count: number;
  mapping_count: number;
  last_synced_at: string | null;
  run_status: string | null;
  run_phase: string | null;
  categories_fetched: number;
  categories_total: number;
  active_job_id: string | null;
}

interface ShippingGroupStatusRow {
  storefront: string;
  count: number;
  lastSyncedAt: string | null;
  /** Vollstaendige Liste fuer den Standard-Auswahl-Selector pro Card. */
  groups: Array<{ id_shipping_group: string; name: string | null }>;
  /** Aktuell als Standard markierte Versandgruppe (aus kaufland.offer_default_profiles,
   *  category_id IS NULL). null wenn noch kein Default gepflegt ist. */
  defaultShippingGroupId: string | null;
}

type StorefrontState = 'active' | 'channel-missing' | 'inactive-available' | 'not-authorised';

interface RowState {
  state: StorefrontState;
  probeMessage?: string | undefined;
  varioLabel?: string | undefined;
  storageId?: string | null | undefined;
  storageLabel?: string | null | undefined;
  storageIds?: string[] | undefined;
  suppressStockPush?: boolean | undefined;
  autoTakeoverOrders?: boolean | undefined;
  paymentMethodId?: string | null | undefined;
  paymentMethodLabel?: string | null | undefined;
  paymentTermId?: string | null | undefined;
  paymentTermLabel?: string | null | undefined;
  deliveryMethodId?: string | null | undefined;
  deliveryMethodLabel?: string | null | undefined;
  deliveryTermId?: string | null | undefined;
  deliveryTermLabel?: string | null | undefined;
}

let categoryPollTimer: ReturnType<typeof setInterval> | null = null;

interface SettingsSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Slot-Renderer für den Vario-App-Tab `/#/settings`
 *  (system.apps.{appId}.tabs). Rendert die Marktplatz-Einstellungen mit eigenem
 *  Kopf (wie Log/Bestellungen) über die volle iframe-Höhe. */
export function renderSettings(ctx: SettingsSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderSettingsTab(ctx.contentEl, { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl }, { standalone: true });
}

export async function renderSettingsTab(host: HTMLElement, ctx: SettingsCtx, opts: { standalone?: boolean } = {}) {
  // Stop any leftover polling from a previous render (tab switch).
  stopCategoryPoll();

  const standalone = !!opts.standalone;
  const appheadHtml = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Kaufland · Einstellungen</div><h1>Marktplatz-Einstellungen</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>Kaufland verbunden</span>
          <button type="button" class="btn btn-primary" id="kl-settings-refresh" title="Marktplatz-Freischaltungen, Kategorien & Versandgruppen-Status neu von Kaufland/Vario laden">
            <span class="v-icon fa-regular fa-rotate-right" id="kl-settings-refresh-icon"></span>Aktualisieren
          </button>
        </div>
      </header>`
    : '';
  const grid = `<div id="kl-status-banner"></div><div id="kl-storefronts" class="mp-grid kl-storefront-grid">Prüfe Freischaltungen …</div>`;
  host.innerHTML = standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${appheadHtml}<div class="scrollarea">${grid}</div></main></div>`
    : `<div class="kf-page" style="height:auto;display:block;">${grid}</div>`;

  if (standalone) {
    const root = host.querySelector<HTMLElement>('.kf-page')!;
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();

    // "Aktualisieren": lädt die komplette Erkennung neu — re-probt Kaufland
    // (neu freigeschaltete Marktplätze erscheinen), zieht Channel-/Kategorie-/
    // Versandgruppen-Status frisch. Re-Entry über renderSettingsTab; die fresh
    // gerenderte Liste zeigt "Prüfe Freischaltungen …" als Lade-Feedback.
    host.querySelector<HTMLButtonElement>('#kl-settings-refresh')?.addEventListener('click', () => {
      const icon = host.querySelector<HTMLElement>('#kl-settings-refresh-icon');
      if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
      void renderSettingsTab(host, ctx, opts);
    });
  }

  const listEl = host.querySelector<HTMLDivElement>('#kl-storefronts')!;
  const bannerEl = host.querySelector<HTMLDivElement>('#kl-status-banner')!;

  // Run channel-status (edge fn) and category-status (RPC) in parallel — both feed the same UI.
  const statusPromise = fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/kaufland-storefronts-available`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: '{}',
  }).then(async (res) => ({ res, body: (await res.json()) as StatusResponse }));
  const categoryStatusPromise = fetchCategoryStatus(ctx);

  let status: StatusResponse;
  try {
    const { res, body } = await statusPromise;
    status = body;
    if (!res.ok || !status.ok) {
      const msg = status.error?.message ?? `HTTP ${res.status}`;
      listEl.innerHTML = '';
      bannerEl.innerHTML = `<p class="install-status install-status--error">Status-Abruf fehlgeschlagen: ${escapeHtml(msg)}</p>`;
      if (status.error?.code === 'NO_INSTALL') {
        listEl.innerHTML = `<p>Noch keine Kaufland-Installation gefunden. Bitte die App über den Vario-Install-Slot installieren.</p>`;
      }
      return;
    }
  } catch (e) {
    listEl.innerHTML = `<p class="install-status install-status--error">Unerwarteter Fehler: ${escapeHtml((e as Error).message)}</p>`;
    return;
  }

  const dbByCode = new Map<string, StoredStorefront>();
  for (const s of status.dbStorefronts ?? []) dbByCode.set(s.storefront.toLowerCase(), s);
  const probeByCode = new Map<string, { ok: boolean; status?: number; message?: string }>();
  for (const p of status.probes ?? []) probeByCode.set(p.storefront, p);
  const varioChannelById = new Map<string, { id: string; label: string }>();
  for (const ch of status.varioChannels ?? []) varioChannelById.set(ch.id, ch);

  if (status.varioError) {
    bannerEl.innerHTML = `<p class="install-status install-status--info">Hinweis: Vario-Channel-Liste konnte nicht abgerufen werden (${escapeHtml(status.varioError)}). Anzeige der Channel-Gesundheit eingeschränkt.</p>`;
  }

  // Storefront-Liste katalog-getrieben aus der Backend-Antwort (Fallback: statische Liste,
  // falls eine ältere Backend-Version noch kein supportedStorefronts mitschickt).
  const COUNTRIES: StorefrontDef[] = (status.supportedStorefronts && status.supportedStorefronts.length)
    ? status.supportedStorefronts.map((s) => ({ code: s.code, label: s.name.replace(/^Kaufland\s+/i, '') }))
    : FALLBACK_STOREFRONTS;

  const states = new Map<string, RowState>();
  // Storefronts, deren Lagerauswahl beim Abgleich mit dem Vario-Kanal gekürzt wurde. Wird nach
  // dem Rendern persistiert und dem Händler gemeldet (siehe persistChannelReconcile weiter unten).
  const reconciled = new Map<string, { ids: string[]; defaultId: string | null; removed: string[] }>();
  for (const c of COUNTRIES) {
    const dbRow = dbByCode.get(c.code);
    const probe = probeByCode.get(c.code);
    const available = !!probe?.ok;
    const varioChannel = dbRow ? varioChannelById.get(dbRow.vario_channel_id) : undefined;

    let state: StorefrontState;
    if (dbRow && varioChannel) {
      state = 'active';
    } else if (dbRow && !varioChannel && !status.varioError) {
      state = 'channel-missing';
    } else if (dbRow) {
      state = 'active';
    } else if (available) {
      state = 'inactive-available';
    } else {
      state = 'not-authorised';
    }
    // Vario ist bei der Lager-Zuordnung führend: hat der Händler dort eine Zuordnung entfernt,
    // fliegt das Lager auch aus unserer Bestandsquelle. `channelStorageIds[code] === null` heißt
    // „nicht lesbar" und ändert bewusst nichts (sonst leert ein Vario-Aussetzer die Auswahl).
    const savedIds = dbRow?.vario_storage_ids ?? (dbRow?.vario_storage_id ? [dbRow.vario_storage_id] : []);
    const sync = reconcileWithChannel(
      savedIds,
      dbRow?.vario_storage_id ?? null,
      status.channelStorageIds?.[c.code],
    );
    if (sync.removed.length > 0) {
      reconciled.set(c.code, { ids: sync.ids, defaultId: sync.defaultId, removed: sync.removed });
    }

    states.set(c.code, {
      state,
      varioLabel: varioChannel?.label ?? dbRow?.vario_channel_label,
      probeMessage: probe && !probe.ok ? `${probe.status ?? ''} ${probe.message ?? ''}`.trim() : undefined,
      storageId: sync.defaultId ?? undefined,
      storageLabel: dbRow?.vario_storage_label,
      storageIds: sync.ids.length > 0 ? sync.ids : undefined,
      suppressStockPush: dbRow?.suppress_stock_push ?? false,
      autoTakeoverOrders: dbRow?.auto_takeover_orders ?? false,
      paymentMethodId: dbRow?.vario_payment_method_id,
      paymentMethodLabel: dbRow?.vario_payment_method_label,
      paymentTermId: dbRow?.vario_payment_term_id,
      paymentTermLabel: dbRow?.vario_payment_term_label,
      deliveryMethodId: dbRow?.vario_delivery_method_id,
      deliveryMethodLabel: dbRow?.vario_delivery_method_label,
      deliveryTermId: dbRow?.vario_delivery_term_id,
      deliveryTermLabel: dbRow?.vario_delivery_term_label,
    });
  }

  // Vario-Lager-Liste vorab laden — best-effort, im Hintergrund. Render erfolgt ohne sie,
  // sobald die Liste da ist, füllen wir die Lager-Panels nach.
  const warehousesPromise = loadVarioWarehouses(ctx).catch((e) => {
    console.warn('[settings] vario-warehouses-list failed', e);
    return [] as VarioWarehouse[];
  });
  // Zahlart + Zahlungsbedingung + Versandart + Lieferbedingung in einem Call laden.
  const paymentPromise = loadVarioPaymentOptions(ctx).catch((e) => {
    console.warn('[settings] vario-payment-options-list failed', e);
    return {
      methods: [] as PaymentOption[],
      terms: [] as PaymentOption[],
      deliveryMethods: [] as PaymentOption[],
      deliveryTerms: [] as PaymentOption[],
    };
  });

  // Reihenfolge: zuerst die für den Händler freigeschalteten Marktplätze
  // (aktiv → channel-missing → inaktiv-aber-freigeschaltet), dann ganz unten
  // die nicht freigeschalteten. Innerhalb einer Gruppe bleibt die COUNTRIES-
  // Reihenfolge (de, cz, sk, pl, at, fr, it) erhalten.
  const STATE_ORDER: Record<StorefrontState, number> = {
    active: 0,
    'channel-missing': 1,
    'inactive-available': 2,
    'not-authorised': 3,
  };
  const sortedCountries = [...COUNTRIES].sort((a, b) => {
    const sa = STATE_ORDER[states.get(a.code)!.state];
    const sb = STATE_ORDER[states.get(b.code)!.state];
    if (sa !== sb) return sa - sb;
    return COUNTRIES.indexOf(a) - COUNTRIES.indexOf(b);
  });
  listEl.innerHTML = sortedCountries.map((c) => renderRow(c, states.get(c.code)!)).join('');

  // Sobald Lager-Liste da ist, alle Lager-Panels füllen — und erst dann den Kanal-Abgleich
  // persistieren, weil hier die Lagernamen für die Meldung auflösbar sind.
  void warehousesPromise.then((warehouses) => {
    cachedWarehouses = warehouses;
    listEl.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      applyWarehousesToMulti(wrap, warehouses);
    });
    for (const [code, sync] of reconciled) {
      void persistChannelReconcile(listEl, ctx, code, sync);
    }
  });
  // Sobald Zahlart-/Zahlungsbedingung-/Versandart-/Lieferbedingung-Listen
  // da sind, alle vier <select>-Familien füllen.
  void paymentPromise.then((opts) => {
    cachedPaymentMethods = opts.methods;
    cachedPaymentTerms = opts.terms;
    cachedDeliveryMethods = opts.deliveryMethods;
    cachedDeliveryTerms = opts.deliveryTerms;
    listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-method-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.methods, currentId);
    });
    listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-term-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.terms, currentId);
    });
    listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-method-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.deliveryMethods, currentId);
    });
    listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-term-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.deliveryTerms, currentId);
    });
  });

  // Aktivieren/Reparieren rendert den Tab danach KOMPLETT neu (derselbe Pfad wie der
  // „Aktualisieren"-Button). Vorher hat der Activate-Flow nur das Badge getauscht und zwei
  // leere Platzhalter angehängt — der ganze Einstellungs-Block (Vario-Lager, die beiden
  // Checkboxen, Zahlart/Zahlungsbedingung/Versandart/Lieferbedingung) fehlte bis zum
  // manuellen Refresh, und der „Aktivieren"-Button blieb stehen. Der Re-Render liest den
  // echten Zustand neu und trifft damit auch den Fall, dass die Aktivierung teilweise
  // durchlief (z. B. `channel-missing`).
  listEl.querySelectorAll<HTMLButtonElement>('button[data-action="activate"], button[data-action="repair"]').forEach((btn) => {
    btn.addEventListener('click', () => activateStorefront(btn, ctx, () => {
      void renderSettingsTab(host, ctx, opts);
    }));
  });
  // Lager: Panel auf/zu; gespeichert wird beim Zuklappen, wenn sich die Auswahl geaendert hat
  // (die uebrigen Selects der Karte speichern on-change — hier waere ein Call pro Klick).
  // Der Icon-Button daneben laedt die Vario-Lager-Liste neu.
  listEl.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
    bindMultiInputs(wrap);
    const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');
    btn?.addEventListener('click', () => {
      const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
      if (panel && panel.hidden) openStoragePanel(wrap, ctx);
      else void closeStoragePanel(wrap, ctx);
    });
  });
  bindStorageGlobalHandlers(ctx);
  listEl.querySelectorAll<HTMLButtonElement>('button[data-action="reload-warehouses"]').forEach((btn) => {
    btn.addEventListener('click', () => reloadWarehouses(btn, ctx));
  });
  listEl.querySelectorAll<HTMLInputElement>('input[data-suppress-stock]').forEach((cb) => {
    cb.addEventListener('change', () => toggleSuppressStock(cb, ctx));
  });
  listEl.querySelectorAll<HTMLInputElement>('input[data-auto-takeover]').forEach((cb) => {
    cb.addEventListener('change', () => toggleAutoTakeover(cb, ctx));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-method-select]').forEach((sel) => {
    sel.addEventListener('change', () => savePaymentMethod(sel, ctx));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-term-select]').forEach((sel) => {
    sel.addEventListener('change', () => savePaymentTerm(sel, ctx));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-method-select]').forEach((sel) => {
    sel.addEventListener('change', () => saveDeliveryMethod(sel, ctx));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-term-select]').forEach((sel) => {
    sel.addEventListener('change', () => saveDeliveryTerm(sel, ctx));
  });

  // Now wire the category-status pass — only the active rows actually have a placeholder for it.
  let categoryRows: CategoryStatusRow[] = [];
  try {
    categoryRows = await categoryStatusPromise;
  } catch (e) {
    console.warn('[settings] category status fetch failed', e);
  }
  applyCategoryStatus(listEl, categoryRows, ctx);

  // If anything is currently syncing, start polling so the user sees live progress.
  if (categoryRows.some((r) => r.active_job_id || r.run_status === 'running')) {
    startCategoryPoll(listEl, ctx);
  }

  // Versandgruppen-Status separat laden — keine Polling-Mechanik, refresh ist
  // manuell und vergleichsweise schnell (1..2s pro Storefront).
  const shippingRows = await fetchShippingGroupStatus(ctx);
  applyShippingGroupStatus(listEl, shippingRows, ctx);
}

// ----- Category status block -----------------------------------------------

async function fetchCategoryStatus(ctx: SettingsCtx): Promise<CategoryStatusRow[]> {
  const { data, error } = await ctx.supabase.rpc('kaufland_get_category_status');
  if (error) {
    console.warn('[settings] kaufland_get_category_status error', error);
    return [];
  }
  return (data ?? []) as CategoryStatusRow[];
}

function applyCategoryStatus(listEl: HTMLElement, rows: CategoryStatusRow[], ctx: SettingsCtx): void {
  const byStorefront = new Map<string, CategoryStatusRow>();
  for (const r of rows) byStorefront.set(r.storefront, r);

  listEl.querySelectorAll<HTMLElement>('.kl-storefront-card').forEach((card) => {
    const code = card.dataset.storefront!;
    const placeholder = card.querySelector<HTMLDivElement>('[data-categories-block]');
    if (!placeholder) return; // Only active cards have one.
    const data = byStorefront.get(code);
    placeholder.innerHTML = renderCategoryBlock(code, data);
    placeholder.querySelectorAll<HTMLButtonElement>('button[data-action="refresh-categories"]').forEach((btn) => {
      btn.addEventListener('click', () => onRefreshCategoriesClick(btn, ctx));
    });
  });
}

function renderCategoryBlock(storefront: string, row: CategoryStatusRow | undefined): string {
  // Design "Kaufland Einstellungen": .mp-stat (Zähler) + .mp-sync (Status) + .mp-btn.
  if (!row || row.kaufland_count === 0) {
    return `
      <div class="mp-sync warn"><span class="d"></span>Kategorien werden gerade aus Kaufland geladen (Hintergrund-Job, wöchentlich) …</div>
    `;
  }

  const kl = row.kaufland_count.toLocaleString('de-DE');
  const v = row.vario_count.toLocaleString('de-DE');
  const diff = row.kaufland_count - row.vario_count;

  if (row.run_status === 'running' || row.active_job_id) {
    const total = row.categories_total > 0 ? row.categories_total : Math.max(0, diff);
    const done = row.categories_fetched;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const phaseLabel = formatPhase(row.run_phase);
    return `
      <div class="mp-stat">${SVG_TAGS}Kategorien: <b>${kl} KL</b> / <b>${v} V</b></div>
      <div class="mp-sync syncing"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span>${escapeHtml(phaseLabel)} · <b>${done.toLocaleString('de-DE')} / ${total.toLocaleString('de-DE')}</b> (${pct} %)</div>
      <div class="mp-note">kann bis zu einer Stunde dauern</div>
    `;
  }

  if (diff > 0) {
    const sub = row.run_status === 'failed'
      ? 'Sync fehlgeschlagen'
      : (row.last_synced_at ? `letzter Sync ${formatRelative(row.last_synced_at)}` : 'noch nicht synchronisiert');
    return `
      <div class="mp-stat">${SVG_TAGS}Kategorien: <b>${kl} KL</b> / <b>${v} V</b></div>
      <div class="mp-sync warn"><span class="d"></span>${diff.toLocaleString('de-DE')} fehlt${diff === 1 ? '' : 'en'} <span class="when">· ${escapeHtml(sub)}</span></div>
      <button type="button" class="mp-btn" data-action="refresh-categories" data-storefront="${storefront}">
        <span class="v-icon fa-regular fa-arrows-rotate" data-icon-default></span>
        Kategorien Aktualisieren
      </button>
    `;
  }

  const sub = row.last_synced_at ? `letzter Sync ${formatRelative(row.last_synced_at)}` : '';
  return `
    <div class="mp-stat">${SVG_TAGS}Kategorien: <b>${kl} KL</b> / <b>${v} V</b></div>
    <div class="mp-sync ok"><span class="d"></span>${SVG_CHECK_SM}synchron${sub ? ` <span class="when">· ${escapeHtml(sub)}</span>` : ''}</div>
    <button type="button" class="mp-btn" data-action="refresh-categories" data-storefront="${storefront}" disabled title="Kategorien sind synchron — nichts zu tun">
      <span class="v-icon fa-regular fa-arrows-rotate" data-icon-default></span>
      Kategorien Aktualisieren
    </button>
  `;
}

function formatPhase(phase: string | null): string {
  if (!phase) return 'läuft';
  if (phase === 'pull') return 'lade Kaufland-Katalog';
  if (phase === 'ensure-tree') return 'Vario-Baum vorbereiten';
  if (phase === 'compare') return 'Vergleich mit Vario';
  if (phase.startsWith('push:level-')) return `Push Ebene ${phase.slice('push:level-'.length)}`;
  return phase;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  if (diffMs < 60_000) return `vor ${Math.max(1, Math.round(diffMs / 1000))} s`;
  if (diffMs < 3_600_000) return `vor ${Math.round(diffMs / 60_000)} min`;
  if (diffMs < 86_400_000) return `vor ${Math.round(diffMs / 3_600_000)} h`;
  return new Date(iso).toLocaleDateString('de-DE');
}

async function onRefreshCategoriesClick(btn: HTMLButtonElement, ctx: SettingsCtx): Promise<void> {
  const storefront = btn.dataset.storefront!;
  btn.disabled = true;
  const icon = btn.querySelector<HTMLElement>('[data-icon-default]');
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';

  const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_category_push', { _storefront: storefront });
  if (error) {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
    showToast(`Konnte Sync nicht starten: ${error.message}`, 'error');
    return;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (row && row.enqueued === false) {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
    showToast('Sync läuft bereits für diesen Marktplatz.', 'info');
    return;
  }

  // Start polling so the inline progress updates.
  const listEl = btn.closest<HTMLDivElement>('.kl-storefront-grid');
  if (listEl) {
    // Erstes Status-Update sofort holen, damit der User direkt sieht dass der Job in der Queue
    // ist — sonst klafft eine 10s-Lücke zwischen RPC-OK und erstem Polling-Tick, in der nur der
    // Button-Spinner läuft und es so wirkt als sei nichts passiert.
    void pollCategoryStatusOnce(listEl, ctx);
    startCategoryPoll(listEl, ctx);
  }
}

async function pollCategoryStatusOnce(listEl: HTMLElement, ctx: SettingsCtx): Promise<void> {
  try {
    const rows = await fetchCategoryStatus(ctx);
    applyCategoryStatus(listEl, rows, ctx);
  } catch (e) {
    console.warn('[settings] initial category status fetch failed', e);
  }
}

function startCategoryPoll(listEl: HTMLElement, ctx: SettingsCtx): void {
  stopCategoryPoll();
  categoryPollTimer = setInterval(async () => {
    try {
      const rows = await fetchCategoryStatus(ctx);
      applyCategoryStatus(listEl, rows, ctx);
      // Stop once nothing's running any more.
      if (!rows.some((r) => r.active_job_id || r.run_status === 'running')) {
        stopCategoryPoll();
      }
    } catch (e) {
      console.warn('[settings] category poll failed', e);
    }
  }, CATEGORY_POLL_INTERVAL_MS);
}

function stopCategoryPoll(): void {
  if (categoryPollTimer) {
    clearInterval(categoryPollTimer);
    categoryPollTimer = null;
  }
}

// ----- Shipping-Groups status block ---------------------------------------

async function fetchShippingGroupStatus(ctx: SettingsCtx): Promise<ShippingGroupStatusRow[]> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('shipping_groups')
    .select('storefront, id_shipping_group, name, fetched_at')
    .order('storefront')
    .order('name');
  if (error) {
    console.warn('[settings] shipping_groups status fetch failed', error.message);
    return [];
  }

  type SgRow = { storefront: string; id_shipping_group: string; name: string | null; fetched_at: string | null };
  const byStorefront = new Map<string, {
    count: number;
    latest: string | null;
    groups: Array<{ id_shipping_group: string; name: string | null }>;
  }>();
  for (const r of (data ?? []) as SgRow[]) {
    const slot = byStorefront.get(r.storefront) ?? { count: 0, latest: null, groups: [] };
    slot.count += 1;
    if (r.fetched_at && (!slot.latest || r.fetched_at > slot.latest)) slot.latest = r.fetched_at;
    slot.groups.push({ id_shipping_group: r.id_shipping_group, name: r.name });
    byStorefront.set(r.storefront, slot);
  }

  // Standard-Versandgruppe je Storefront aus kaufland.offer_default_profiles
  // (category_id IS NULL = globales Profil). Parallel pro storefront via Get-RPC.
  const storefronts = Array.from(byStorefront.keys());
  const profilePromises = storefronts.map((sf) =>
    ctx.supabase.rpc('kaufland_get_offer_default_profile', { _storefront: sf }),
  );
  const profileResults = await Promise.all(profilePromises);
  const defaultBySf = new Map<string, string | null>();
  for (let i = 0; i < storefronts.length; i++) {
    const sf = storefronts[i]!;
    const res = profileResults[i]!;
    if (res.error) {
      console.warn('[settings] offer_default_profile fetch failed for', sf, res.error.message);
      defaultBySf.set(sf, null);
      continue;
    }
    const payload = (res.data ?? {}) as { global?: { id_shipping_group?: string | null } | null };
    defaultBySf.set(sf, payload.global?.id_shipping_group ?? null);
  }

  return storefronts.map((storefront) => {
    const slot = byStorefront.get(storefront)!;
    return {
      storefront,
      count: slot.count,
      lastSyncedAt: slot.latest,
      groups: slot.groups,
      defaultShippingGroupId: defaultBySf.get(storefront) ?? null,
    };
  });
}

function applyShippingGroupStatus(
  listEl: HTMLElement,
  rows: ShippingGroupStatusRow[],
  ctx: SettingsCtx,
): void {
  const byStorefront = new Map<string, ShippingGroupStatusRow>();
  for (const r of rows) byStorefront.set(r.storefront, r);

  listEl.querySelectorAll<HTMLElement>('.kl-storefront-card').forEach((card) => {
    const code = card.dataset.storefront!;
    const placeholder = card.querySelector<HTMLDivElement>('[data-shipping-groups-block]');
    if (!placeholder) return; // Nur aktive Cards haben den Block.
    const row = byStorefront.get(code);
    placeholder.innerHTML = renderShippingGroupBlock(code, row);
    placeholder.querySelectorAll<HTMLButtonElement>('button[data-action="refresh-shipping-groups"]').forEach((btn) => {
      btn.addEventListener('click', () => onRefreshShippingGroupsClick(btn, ctx));
    });
    placeholder.querySelectorAll<HTMLSelectElement>('select[data-action="set-default-shipping-group"]').forEach((sel) => {
      sel.addEventListener('change', () => void onDefaultShippingGroupChange(sel, ctx));
    });
  });
}

function renderShippingGroupBlock(storefront: string, row: ShippingGroupStatusRow | undefined): string {
  const count = row?.count ?? 0;
  const sub = row?.lastSyncedAt
    ? `letzter Sync ${formatRelative(row.lastSyncedAt)}`
    : '';

  // Design "Kaufland Einstellungen": .mp-stat + .mp-sync + optionaler Standard-Select + .mp-btn.
  if (count === 0) {
    return `
      <div class="mp-stat">${SVG_TRUCK}Versandgruppen: <b>0</b></div>
      <div class="mp-sync warn"><span class="d"></span>noch nicht geladen <span class="when">· bitte aktualisieren</span></div>
      <button type="button" class="mp-btn" data-action="refresh-shipping-groups" data-storefront="${storefront}">
        <span class="v-icon fa-regular fa-arrows-rotate" data-icon-default></span>
        Versandgruppen Aktualisieren
      </button>
    `;
  }

  const countLabel = `${count} Versandgruppe${count === 1 ? '' : 'n'}`;
  const groups = row?.groups ?? [];
  const defaultId = row?.defaultShippingGroupId ?? '';
  const defaultSelect = groups.length > 0 ? `
    <div class="mp-field">
      <span class="mp-flab">Standard-Versandgruppe</span>
      <div class="mp-select">
        <select id="kl-sg-default-${escapeHtml(storefront)}"
                data-action="set-default-shipping-group"
                data-storefront="${escapeHtml(storefront)}"
                data-previous-value="${escapeHtml(defaultId)}">
          <option value="" ${defaultId === '' ? 'selected' : ''}>— kein Standard —</option>
          ${groups.map((g) => {
            const label = g.name ?? g.id_shipping_group;
            const isSelected = g.id_shipping_group === defaultId ? 'selected' : '';
            return `<option value="${escapeHtml(g.id_shipping_group)}" ${isSelected}>${escapeHtml(label)}</option>`;
          }).join('')}
        </select>
      </div>
    </div>
    <div class="mp-note">Wird im Aufschalten-Lister automatisch vorbelegt.</div>
  ` : '';

  return `
    <div class="mp-stat">${SVG_TRUCK}Versandgruppen: <b>${count}</b></div>
    <div class="mp-sync ok"><span class="d"></span>${SVG_CHECK_SM}${escapeHtml(countLabel)}${sub ? ` <span class="when">· ${escapeHtml(sub)}</span>` : ''}</div>
    ${defaultSelect}
    <button type="button" class="mp-btn" data-action="refresh-shipping-groups" data-storefront="${storefront}">
      <span class="v-icon fa-regular fa-arrows-rotate" data-icon-default></span>
      Versandgruppen Aktualisieren
    </button>
  `;
}

async function onDefaultShippingGroupChange(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const newDefault = sel.value || null;
  const previousValue = sel.dataset.previousValue ?? '';
  sel.disabled = true;

  // Aktuelles globales Profil holen — wir mergen die Aenderung,
  // damit Save (Upsert) keine anderen Felder auf NULL setzt.
  const getRes = await ctx.supabase.rpc('kaufland_get_offer_default_profile', { _storefront: storefront });
  if (getRes.error) {
    showToast(`Standard konnte nicht geladen werden: ${getRes.error.message}`, 'error');
    sel.value = previousValue;
    sel.disabled = false;
    return;
  }
  const profile = (getRes.data ?? {}) as { global?: Record<string, unknown> | null };
  const current = profile.global ?? {};

  const fields = {
    condition: current['condition'] ?? null,
    handling_time: current['handling_time'] ?? null,
    id_shipping_group: newDefault,
    vat_indicator: current['vat_indicator'] ?? null,
    field_mappings: current['field_mappings'] ?? {},
  };

  const saveRes = await ctx.supabase.rpc('kaufland_save_offer_default_profile', {
    _storefront: storefront,
    _category_id: null,
    _fields: fields,
  });
  if (saveRes.error) {
    showToast(`Standard speichern fehlgeschlagen: ${saveRes.error.message}`, 'error');
    sel.value = previousValue;
    sel.disabled = false;
    return;
  }

  sel.dataset.previousValue = sel.value;
  sel.disabled = false;
  const label = sel.options[sel.selectedIndex]?.text ?? '';
  showToast(newDefault
    ? `Standard auf „${label}" gesetzt.`
    : 'Standard-Versandgruppe entfernt.',
    'success');
}

async function onRefreshShippingGroupsClick(btn: HTMLButtonElement, ctx: SettingsCtx): Promise<void> {
  const storefront = btn.dataset.storefront!;
  btn.disabled = true;
  const icon = btn.querySelector<HTMLElement>('[data-icon-default]');
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';

  const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_refresh_shipping_groups', {
    _storefront: storefront,
  });
  if (error) {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
    showToast(`Konnte Sync nicht starten: ${error.message}`, 'error');
    return;
  }
  const row = Array.isArray(data) ? data[0] : data;
  const jobId = (row as { job_id?: string } | null)?.job_id;
  if (!jobId) {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
    showToast('Sync-Job konnte nicht angelegt werden.', 'error');
    return;
  }

  try {
    const result = await awaitJob(ctx.supabase, jobId, 60_000);
    const r = (result?.result ?? {}) as { groupsTotal?: number; errors?: unknown[] };
    const groupsTotal = r.groupsTotal ?? 0;
    const hasErrors = Array.isArray(r.errors) && r.errors.length > 0;
    if (hasErrors) {
      showToast('Sync mit Fehler abgeschlossen — siehe Konsole / Worker-Log.', 'error');
      console.warn('[settings] refresh_shipping_groups errors:', r.errors);
    } else if (groupsTotal === 0) {
      showToast('Sync abgeschlossen — Kaufland hat keine Versandgruppen geliefert. Bitte im Kaufland-Sellerportal mindestens eine anlegen.', 'info');
    } else {
      showToast(`${groupsTotal} Versandgruppe${groupsTotal === 1 ? '' : 'n'} synchronisiert.`, 'success');
    }
  } catch (e) {
    showToast(`Sync fehlgeschlagen: ${(e as Error).message}`, 'error');
  } finally {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
  }

  // Frische Daten in den Block einspielen.
  const listEl = btn.closest<HTMLDivElement>('.kl-storefront-grid');
  if (listEl) {
    const rows = await fetchShippingGroupStatus(ctx);
    applyShippingGroupStatus(listEl, rows, ctx);
  }
}

// ----- Row rendering -------------------------------------------------------
// Markup folgt der Design-Reference 2026-05-22: kl-storefront-card mit
// kl-storefront-head (Flag + Title + Status-Pill) + State-spezifischem Body.
// Modifier-Klasse `is-muted` ersetzt das BEM-`--muted`.

function renderRow(c: StorefrontDef, s: RowState): string {
  // Marktplatz-Badge rechts oben (Design "Kaufland Einstellungen"): Punkt + Text.
  // `.kl-storefront-status` bleibt als Hook-Klasse (activateStorefront tauscht ihn).
  let badge = '';
  switch (s.state) {
    case 'active':
      badge = `<span class="mp-badge ok"><span class="d"></span>Aktiv</span>`;
      break;
    case 'channel-missing':
      badge = `<span class="mp-badge warn" title="DB sagt aktiv, aber Vario hat keinen passenden Sales-Channel mehr"><span class="d"></span>In Vario gelöscht</span>`;
      break;
    case 'inactive-available':
      badge = `<span class="mp-badge off"><span class="d"></span>Inaktiv</span>`;
      break;
    case 'not-authorised':
      badge = `<span class="mp-badge off" title="${escapeHtml(s.probeMessage ?? '')}"><span class="d"></span>Nicht freigeschaltet</span>`;
      break;
  }

  let bodyHtml = '';
  switch (s.state) {
    case 'active':
      bodyHtml = renderActiveBody(c, s);
      break;
    case 'channel-missing':
      bodyHtml = renderChannelMissingBody(c, s);
      break;
    case 'inactive-available':
      bodyHtml = renderInactiveBody(c);
      break;
    case 'not-authorised':
      bodyHtml = renderNotAuthBody();
      break;
  }

  const locked = s.state === 'not-authorised' ? ' locked' : '';
  return `
    <article class="mp-card${locked} kl-storefront-card" data-storefront="${c.code}">
      <div class="mp-head">
        <span class="mp-code">${escapeHtml(c.code.toUpperCase())}</span>
        <span class="mp-name">Kaufland ${escapeHtml(c.label)}</span>
        <span class="kl-storefront-status">${badge}</span>
      </div>
      ${bodyHtml}
    </article>
  `;
}

function renderActiveBody(c: StorefrontDef, s: RowState): string {
  return `
    ${renderStorefrontSettings(c.code, s)}
    <div class="mp-sec border">
      <div class="mp-lab">Kategorien-Sync</div>
      <div data-categories-block>Lade Kategorien-Status …</div>
    </div>
    <div class="mp-sec border">
      <div class="mp-lab">Versandgruppen-Sync</div>
      <div data-shipping-groups-block>Lade Versandgruppen-Status …</div>
    </div>
  `;
}

function renderChannelMissingBody(c: StorefrontDef, s: RowState): string {
  // Wir behalten den Settings-Block (Lager etc. sind unabhängig vom Vario-
  // Channel), und ergänzen oben einen Repair-Aufruf — der aus dem Verlust
  // herauskommt, wenn ein Operator den Channel in Vario gelöscht hat.
  return `
    <div class="mp-sec"><div class="mp-action">
      <button type="button" class="mp-btn" data-action="repair" data-storefront="${c.code}">
        <span class="v-icon fa-regular fa-rotate-right" data-icon-default></span>
        Channel neu anlegen
      </button>
      <span class="mp-msg" data-msg></span>
    </div></div>
    ${renderStorefrontSettings(c.code, s)}
  `;
}

function renderInactiveBody(c: StorefrontDef): string {
  return `
    <div class="mp-sec"><div class="mp-action">
      <p class="mp-hint">Für diesen Seller-Account freigeschaltet, aber noch nicht als Sales-Channel in Vario angelegt.</p>
      <button type="button" class="mp-btn" data-action="activate" data-storefront="${c.code}">
        <span class="v-icon fa-regular fa-plus" data-icon-default></span>
        Aktivieren
      </button>
      <span class="mp-msg" data-msg></span>
    </div></div>
  `;
}

function renderNotAuthBody(): string {
  return `
    <div class="mp-locked">
      ${SVG_LOCK}
      <p>Dieser Marktplatz ist für deinen Seller-Account aktuell nicht freigeschaltet.</p>
    </div>
  `;
}

function renderStorefrontSettings(code: string, s: RowState): string {
  const storageId = s.storageId ?? '';
  const storageLabel = s.storageLabel ?? '';
  const paymentMethodId = s.paymentMethodId ?? '';
  const paymentMethodLabel = s.paymentMethodLabel ?? '';
  const paymentTermId = s.paymentTermId ?? '';
  const paymentTermLabel = s.paymentTermLabel ?? '';
  const deliveryMethodId = s.deliveryMethodId ?? '';
  const deliveryMethodLabel = s.deliveryMethodLabel ?? '';
  const deliveryTermId = s.deliveryTermId ?? '';
  const deliveryTermLabel = s.deliveryTermLabel ?? '';
  const suppress = s.suppressStockPush === true;
  const autoOn = s.autoTakeoverOrders === true;
  const storageIds = s.storageIds ?? (storageId ? [storageId] : []);
  const storagePanelHtml = cachedWarehouses
    ? renderWarehousePanel(code, cachedWarehouses, storageIds, storageId || null)
    : '';
  const storageSummary = cachedWarehouses
    ? warehouseSummary(storageIds, storageId || null, labelOfWarehouseId)
    : 'Lade Vario-Lager …';
  const methodOptionsHtml = cachedPaymentMethods
    ? renderPaymentOptions(cachedPaymentMethods, paymentMethodId)
    : `<option value="" disabled selected>Lade Zahlarten …</option>`;
  const termOptionsHtml = cachedPaymentTerms
    ? renderPaymentOptions(cachedPaymentTerms, paymentTermId)
    : `<option value="" disabled selected>Lade Zahlungsbedingungen …</option>`;
  const deliveryMethodOptionsHtml = cachedDeliveryMethods
    ? renderPaymentOptions(cachedDeliveryMethods, deliveryMethodId)
    : `<option value="" disabled selected>Lade Versandarten …</option>`;
  const deliveryTermOptionsHtml = cachedDeliveryTerms
    ? renderPaymentOptions(cachedDeliveryTerms, deliveryTermId)
    : `<option value="" disabled selected>Lade Lieferbedingungen …</option>`;
  // Verstecke Label-Hinweis (escapeHtml) für Storage/Lager-Label.
  // Hidden-input-Data-Attribute reichen für die Backend-Calls.
  return `
    <div data-settings-block>

      <div class="mp-sec">
        <div class="mp-lab">Bestand</div>
        <div class="mp-row">
          <div class="mp-multi" data-storage-multi data-storefront="${code}"
               data-selected-ids="${escapeHtml(storageIds.join(','))}"
               data-default-id="${escapeHtml(storageId)}"
               data-current-label="${escapeHtml(storageLabel)}">
            <button type="button" class="mp-multi-btn" id="kl-storage-${code}"
                    data-storage-toggle aria-expanded="false"
                    title="Vario-Lager auswählen — der gemeldete Bestand ist die Summe der markierten Lager. Das Standard-Lager ist Ziel für übernommene Aufträge.">
              <span data-storage-summary>${escapeHtml(storageSummary)}</span>
            </button>
            <div class="mp-multi-panel" data-storage-panel hidden>${storagePanelHtml}</div>
          </div>
          <button type="button" class="mp-iconbtn"
                  data-action="reload-warehouses" data-storefront="${code}"
                  title="Vario-Lager-Liste neu laden">
            <span class="v-icon fa-regular fa-rotate-right" data-icon-default></span>
          </button>
        </div>
        <div class="mp-msg" data-storage-msg></div>

        <label class="mp-check" title="Stock-Sync-Worker überspringt diese Storefront. Preise und Status laufen weiter.">
          <input type="checkbox" data-suppress-stock data-storefront="${code}" ${suppress ? 'checked' : ''} />
          <span><b>Keine Lagerbestand-Änderungen an Kaufland senden</b><em>Stock-Sync-Worker überspringt diese Storefront. Preise und Status laufen weiter.</em></span>
        </label>
        <div class="mp-msg" data-suppress-msg></div>
      </div>

      <div class="mp-sec border">
        <div class="mp-lab">Auftragsübernahme</div>

        <label class="mp-check"
               title="Bestellungen werden automatisch übernommen + gespeichert, sobald ihr Status auf „zu versenden" wechselt. Bestellungen im Status „offen" bleiben unberührt, bis Kaufland den Versandfreigabe-Wechsel meldet. Folgebelege (Lieferschein, Rechnung) legt der Händler in Vario manuell an — sie werden über Webhooks zurücksynchronisiert.">
          <input type="checkbox" data-auto-takeover data-storefront="${code}" ${autoOn ? 'checked' : ''} />
          <span><b>Bestellungen automatisch übernehmen</b><em>Übernahme + Speichern startet automatisch, sobald Kaufland den Status auf „zu versenden" setzt.</em></span>
        </label>
        <div class="mp-msg" data-auto-takeover-msg></div>

        <div class="mp-field">
          <span class="mp-flab">Zahlart</span>
          <div class="mp-select">
            <select id="kl-payment-method-${code}"
                    data-payment-method-select data-storefront="${code}"
                    data-current-id="${escapeHtml(paymentMethodId)}"
                    data-current-label="${escapeHtml(paymentMethodLabel)}"
                    title="Vario-Zahlart für übernommene Aufträge">
              ${methodOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-payment-method-msg></div>
        </div>

        <div class="mp-field">
          <span class="mp-flab">Zahlungsbedingung</span>
          <div class="mp-select">
            <select id="kl-payment-term-${code}"
                    data-payment-term-select data-storefront="${code}"
                    data-current-id="${escapeHtml(paymentTermId)}"
                    data-current-label="${escapeHtml(paymentTermLabel)}"
                    title="Vario-Zahlungsbedingung für übernommene Aufträge">
              ${termOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-payment-term-msg></div>
        </div>

        <div class="mp-field">
          <span class="mp-flab">Standard Versandart</span>
          <div class="mp-select">
            <select id="kl-delivery-method-${code}"
                    data-delivery-method-select data-storefront="${code}"
                    data-current-id="${escapeHtml(deliveryMethodId)}"
                    data-current-label="${escapeHtml(deliveryMethodLabel)}"
                    title="Vario-Versandart für übernommene Aufträge">
              ${deliveryMethodOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-delivery-method-msg></div>
        </div>

        <div class="mp-field">
          <span class="mp-flab">Lieferbedingung</span>
          <div class="mp-select">
            <select id="kl-delivery-term-${code}"
                    data-delivery-term-select data-storefront="${code}"
                    data-current-id="${escapeHtml(deliveryTermId)}"
                    data-current-label="${escapeHtml(deliveryTermLabel)}"
                    title="Vario-Lieferbedingung für übernommene Aufträge">
              ${deliveryTermOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-delivery-term-msg></div>
        </div>
      </div>
    </div>
  `;
}

/** Ein Lager-Label für die Anzeige (DisplayName → Nummer → ID). */
function warehouseLabel(w: VarioWarehouse): string {
  return w.displayName ?? w.number ?? w.id;
}

/** Label-Auflösung über die zuletzt geladene Lagerliste; unbekannte ID → ID selbst. */
function labelOfWarehouseId(id: string): string {
  const hit = cachedWarehouses?.find((w) => w.id === id);
  return hit ? warehouseLabel(hit) : id;
}

/** Panel-Inhalt: je Lager eine Checkbox (Auswahl) + ein Radio (Standard). Nicht markierte
 *  Lager können nicht Standard sein → ihr Radio ist disabled. */
function renderWarehousePanel(
  code: string,
  warehouses: VarioWarehouse[],
  selectedIds: string[],
  defaultId: string | null,
): string {
  if (warehouses.length === 0) {
    return `<span class="mp-multi-empty">Keine Vario-Lager gefunden — bitte in Vario anlegen.</span>`;
  }
  const sel = new Set(selectedIds);
  const def = resolveDefaultSelection(selectedIds, defaultId);
  return warehouses
    .map((w) => {
      const label = warehouseLabel(w);
      const checked = sel.has(w.id);
      return `<div class="mp-multi-opt">
        <label class="mp-multi-pick">
          <input type="checkbox" data-storage-cb value="${escapeHtml(w.id)}" data-label="${escapeHtml(label)}"${checked ? ' checked' : ''} />
          <span class="mp-multi-name">${escapeHtml(label)}${w.salesStorage ? `<span class="mp-multi-tag">· Verkauf</span>` : ''}</span>
        </label>
        <label class="mp-multi-def" title="Standard-Lager: Ziel für übernommene Aufträge und Varios defaultSalesStorage">
          <input type="radio" data-storage-radio name="kl-wh-def-${escapeHtml(code)}" value="${escapeHtml(w.id)}"${w.id === def ? ' checked' : ''}${checked ? '' : ' disabled'} />Standard
        </label>
      </div>`;
    })
    .join('');
}

/** Aktueller Stand AUS DEN INPUTS (nicht der gespeicherte). */
function readMultiState(wrap: HTMLElement): { ids: string[]; defaultId: string | null; defaultLabel: string | null } {
  const ids = Array.from(wrap.querySelectorAll<HTMLInputElement>('input[data-storage-cb]'))
    .filter((cb) => cb.checked)
    .map((cb) => cb.value);
  const radio = wrap.querySelector<HTMLInputElement>('input[data-storage-radio]:checked');
  const defaultId = resolveDefaultSelection(ids, radio?.value ?? null);
  const defaultCb = defaultId
    ? wrap.querySelector<HTMLInputElement>(`input[data-storage-cb][value="${CSS.escape(defaultId)}"]`)
    : null;
  return { ids, defaultId, defaultLabel: defaultCb?.getAttribute('data-label') ?? null };
}

/** Radios an die Checkboxen angleichen (nicht markiert → nicht wählbar) und die
 *  Zusammenfassung im Button nachziehen. */
function syncMultiUi(wrap: HTMLElement): void {
  const { ids, defaultId } = readMultiState(wrap);
  wrap.querySelectorAll<HTMLInputElement>('input[data-storage-radio]').forEach((radio) => {
    const cb = wrap.querySelector<HTMLInputElement>(`input[data-storage-cb][value="${CSS.escape(radio.value)}"]`);
    const on = !!cb?.checked;
    radio.disabled = !on;
    if (!on) radio.checked = false;
  });
  if (defaultId) {
    const target = wrap.querySelector<HTMLInputElement>(`input[data-storage-radio][value="${CSS.escape(defaultId)}"]`);
    if (target && !target.checked) target.checked = true;
  }
  const summaryEl = wrap.querySelector<HTMLElement>('[data-storage-summary]');
  if (summaryEl) summaryEl.textContent = warehouseSummary(ids, defaultId, labelOfWarehouseId);
}

/** Panel einer Karte (neu) rendern — nach dem Laden/Neuladen der Lagerliste. Nimmt als
 *  Ausgangsauswahl den GESPEICHERTEN Stand aus dem dataset, nicht die alten Inputs. */
function applyWarehousesToMulti(wrap: HTMLElement, warehouses: VarioWarehouse[]): void {
  const code = wrap.dataset.storefront ?? '';
  const savedIds = (wrap.dataset.selectedIds ?? '').split(',').filter((s) => s.length > 0);
  const savedDefault = wrap.dataset.defaultId ?? null;
  const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
  if (!panel) return;
  panel.innerHTML = renderWarehousePanel(code, warehouses, savedIds, savedDefault || null);
  bindMultiInputs(wrap);
  syncMultiUi(wrap);
}

/** Checkbox-/Radio-Listener nach jedem Panel-Render neu setzen (innerHTML ersetzt die Nodes). */
function bindMultiInputs(wrap: HTMLElement): void {
  wrap.querySelectorAll<HTMLInputElement>('input[data-storage-cb], input[data-storage-radio]')
    .forEach((inp) => inp.addEventListener('change', () => syncMultiUi(wrap)));
}

function renderPaymentOptions(opts: PaymentOption[], currentId: string): string {
  if (opts.length === 0) {
    return `<option value="" disabled selected>Keine Einträge gefunden</option>`;
  }
  const out = [`<option value="">— bitte auswählen —</option>`];
  for (const o of opts) {
    const selected = o.id === currentId ? ' selected' : '';
    out.push(`<option value="${escapeHtml(o.id)}"${selected}>${escapeHtml(o.label)}</option>`);
  }
  return out.join('');
}

function applyPaymentOptionsToSelect(
  sel: HTMLSelectElement,
  opts: PaymentOption[],
  currentId: string,
): void {
  sel.innerHTML = renderPaymentOptions(opts, currentId);
}

async function loadVarioPaymentOptions(
  ctx: SettingsCtx,
): Promise<{
  methods: PaymentOption[];
  terms: PaymentOption[];
  deliveryMethods: PaymentOption[];
  deliveryTerms: PaymentOption[];
}> {
  const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-payment-options-list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: '{}',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`vario-payment-options-list ${res.status}: ${body.slice(0, 300)}`);
  }
  const body = (await res.json()) as {
    ok?: boolean;
    methods?: PaymentOption[];
    terms?: PaymentOption[];
    deliveryMethods?: PaymentOption[];
    deliveryTerms?: PaymentOption[];
    error?: { message: string };
  };
  if (!body.ok) {
    throw new Error(body.error?.message ?? 'vario-payment-options-list: unbekannter Fehler');
  }
  return {
    methods: body.methods ?? [],
    terms: body.terms ?? [],
    deliveryMethods: body.deliveryMethods ?? [],
    deliveryTerms: body.deliveryTerms ?? [],
  };
}

async function savePaymentMethod(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-payment-method-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Zahlart auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }

  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_payment_method', {
    _storefront: storefront,
    _method_id: id,
    _method_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      sel.dataset['currentId'] = id;
      msgEl.textContent = '';
      msgEl.className = 'mp-msg';
    }
  }
}

async function savePaymentTerm(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-payment-term-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Zahlungsbedingung auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }
  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_payment_term', {
    _storefront: storefront,
    _term_id: id,
    _term_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = 'Zahlungsbedingung gespeichert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

async function saveDeliveryMethod(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-delivery-method-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Versandart auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }
  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_delivery_method', {
    _storefront: storefront,
    _method_id: id,
    _method_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = 'Versandart gespeichert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

async function saveDeliveryTerm(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-delivery-term-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Lieferbedingung auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }
  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_delivery_term', {
    _storefront: storefront,
    _term_id: id,
    _term_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = 'Lieferbedingung gespeichert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

async function loadVarioWarehouses(ctx: SettingsCtx): Promise<VarioWarehouse[]> {
  const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-warehouses-list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: '{}',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`vario-warehouses-list ${res.status}: ${body.slice(0, 300)}`);
  }
  const body = (await res.json()) as { ok?: boolean; warehouses?: VarioWarehouse[]; error?: { message: string } };
  if (!body.ok || !body.warehouses) {
    throw new Error(body.error?.message ?? 'vario-warehouses-list: unbekannter Fehler');
  }
  return body.warehouses;
}

/**
 * Aktiviert eine Storefront (oder legt bei `repair` den Vario-Channel neu an) und rendert den
 * Tab danach über `onActivated` komplett neu. Der Re-Render ist der Kern: eine frisch aktivierte
 * Karte braucht `renderActiveBody` (Einstellungs-Block + Kategorien-/Versandgruppen-Sync) und
 * darf den „Aktivieren"-Button nicht mehr zeigen — beides lässt sich nicht sinnvoll in die
 * bestehende `inactive`-Karte hineinflicken.
 */
async function activateStorefront(btn: HTMLButtonElement, ctx: SettingsCtx, onActivated: () => void) {
  const storefront = btn.dataset.storefront!;
  const card = btn.closest<HTMLElement>('.kl-storefront-card')!;
  const msgEl = card.querySelector<HTMLSpanElement>('[data-msg]');
  const icon = btn.querySelector<HTMLElement>('[data-icon-default]');
  const originalIconClass = icon?.className ?? 'v-icon fa-regular fa-plus';

  btn.disabled = true;
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
  if (msgEl) {
    msgEl.textContent = btn.dataset.action === 'repair' ? 'Lege neuen Channel an …' : 'Aktiviere …';
    msgEl.className = 'mp-msg info';
  }

  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/kaufland-storefront-add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify({ storefront }),
    });
    const payload = (await res.json()) as StorefrontAddResponse;
    if (!res.ok || !payload.ok) {
      const probe = payload.error?.probe;
      const probeNote = probe && !probe.ok ? ` (${probe.status ?? ''} ${probe.message ?? ''})`.trim() : '';
      const message = `${payload.error?.message ?? `HTTP ${res.status}`}${probeNote}`;
      if (msgEl) {
        msgEl.textContent = message;
        msgEl.className = 'mp-msg err';
      }
      btn.disabled = false;
      if (icon) icon.className = originalIconClass;
      return;
    }

    // Sofort-Feedback am Badge, damit die Karte nicht „unverändert" wirkt, während der
    // Re-Render die Freischaltungen neu prüft (der zeigt kurz „Prüfe Freischaltungen …").
    const statusEl = card.querySelector<HTMLElement>('.kl-storefront-status');
    if (statusEl) {
      statusEl.innerHTML = `<span class="mp-badge ok"><span class="d"></span>Aktiv</span>`;
    }
    if (msgEl) {
      msgEl.textContent = payload.alreadyPresent ? 'War bereits aktiv.' : 'Aktiviert.';
      msgEl.className = 'mp-msg ok';
    }
    showToast(
      payload.alreadyPresent
        ? `${payload.label ?? storefront.toUpperCase()} war bereits aktiv.`
        : `${payload.label ?? storefront.toUpperCase()} aktiviert.`,
      'success',
    );

    // Kompletter Re-Render: erst danach hat die Karte ihren Einstellungs-Block
    // (Vario-Lager, Checkboxen, Zahlart/Zahlungsbedingung/Versandart/Lieferbedingung)
    // sowie die Kategorien-/Versandgruppen-Blöcke — und den „Aktivieren"-Button nicht mehr.
    onActivated();
  } catch (e) {
    if (msgEl) {
      msgEl.textContent = `Fehler: ${(e as Error).message}`;
      msgEl.className = 'mp-msg err';
    }
    btn.disabled = false;
    if (icon) icon.className = originalIconClass;
  }
}

/** Panel öffnen. Andere offene Panels schließen (und dabei speichern, falls geändert). */
function openStoragePanel(wrap: HTMLElement, ctx: SettingsCtx): void {
  document.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((other) => {
    if (other !== wrap) void closeStoragePanel(other, ctx);
  });
  const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
  const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');
  if (!panel || !btn) return;
  panel.hidden = false;
  btn.setAttribute('aria-expanded', 'true');
}

/** Panel schließen und speichern, wenn die Auswahl vom gespeicherten Stand abweicht. */
async function closeStoragePanel(wrap: HTMLElement, ctx: SettingsCtx): Promise<void> {
  const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
  const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');
  if (!panel || panel.hidden) return;
  panel.hidden = true;
  btn?.setAttribute('aria-expanded', 'false');

  // Solange keine Checkbox gerendert ist (Lagerliste noch nicht geladen oder Abruf
  // fehlgeschlagen), gibt es gar keine Nutzer-Auswahl. Ohne diesen Guard wuerde ein
  // Klick auf das leere Panel die gespeicherte Zuordnung mit [] ueberschreiben.
  if (!wrap.querySelector('input[data-storage-cb]')) return;

  const { ids, defaultId, defaultLabel } = readMultiState(wrap);
  const savedIds = (wrap.dataset.selectedIds ?? '').split(',').filter((s) => s.length > 0);
  const savedDefault = wrap.dataset.defaultId ?? '';
  const changed =
    ids.length !== savedIds.length ||
    ids.some((id) => !savedIds.includes(id)) ||
    (defaultId ?? '') !== savedDefault;
  if (!changed) return;
  await saveStorefrontStorages(wrap, ctx, ids, defaultId, defaultLabel);
}

/** Schreibt Mehrfachauswahl + Standard-Lager über `kaufland_set_storefront_warehouses`
 *  (PLURAL — der alte Singular kann nur ein Lager und existiert nur noch als Delegator
 *  für Bundles vor diesem Deploy).
 *  Leere Auswahl ist erlaubt und leert die Zuordnung — die Vario-Kanal-Zuordnung bleibt
 *  bestehen (das Modell ist additiv). */
async function saveStorefrontStorages(
  wrap: HTMLElement,
  ctx: SettingsCtx,
  ids: string[],
  defaultId: string | null,
  defaultLabel: string | null,
): Promise<void> {
  const storefront = wrap.dataset.storefront!;
  const card = wrap.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-storage-msg]');
  const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');

  if (btn) btn.disabled = true;
  if (msgEl) {
    msgEl.textContent = ids.length > 0
      ? 'Speichere und ordne in Vario zu …'
      : 'Lagerauswahl wird geleert …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_warehouses', {
    _storefront: storefront,
    _storage_ids: ids,
    _default_storage_id: defaultId,
    _default_label: defaultLabel,
  });

  if (btn) btn.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else if (ids.length === 0) {
      msgEl.textContent = 'Lagerauswahl geleert. Die Zuordnung am Vario-Kanal bleibt bestehen.';
      msgEl.className = 'mp-msg ok';
    } else {
      msgEl.textContent = `${ids.length} Lager gespeichert. Zuordnung in Vario läuft im Hintergrund.`;
      msgEl.className = 'mp-msg ok';
    }
  }

  // Gespeicherten Stand im dataset nachfuehren, sonst gilt beim naechsten Schliessen
  // alles erneut als geaendert.
  if (!error) {
    wrap.dataset['selectedIds'] = ids.join(',');
    wrap.dataset['defaultId'] = defaultId ?? '';
  }
}

/**
 * Persistiert den Kanal-Abgleich: Lager, die dem Vario-Verkaufskanal nicht mehr zugeordnet sind,
 * fliegen aus der gespeicherten Auswahl. Vario ist bei der Zuordnung führend.
 *
 * Läuft beim Öffnen der Einstellungen, nicht im Hintergrund — die Anzeige zeigt den gekürzten
 * Stand also sofort, die DB folgt Millisekunden später. Der Händler bekommt eine Meldung, weil
 * eine stillschweigend geänderte Bestandsquelle die Menge bei Kaufland verändert.
 *
 * Bewusst KEIN Aufruf, wenn nichts wegfiel: die Funktion wird nur für Storefronts mit
 * `removed.length > 0` gerufen, sonst gäbe es bei jedem Öffnen einen Schreibvorgang plus einen
 * `vario.ensure_channel_storages`-Job.
 */
async function persistChannelReconcile(
  listEl: HTMLElement,
  ctx: SettingsCtx,
  storefront: string,
  sync: { ids: string[]; defaultId: string | null; removed: string[] },
): Promise<void> {
  const card = listEl.querySelector<HTMLElement>(`.kl-storefront-card[data-storefront="${CSS.escape(storefront)}"]`);
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-storage-msg]');
  const namen = sync.removed.map((id) => labelOfWarehouseId(id)).join(', ');

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_warehouses', {
    _storefront: storefront,
    _storage_ids: sync.ids,
    _default_storage_id: sync.defaultId,
    _default_label: sync.defaultId ? labelOfWarehouseId(sync.defaultId) : null,
  });

  if (!msgEl) return;
  if (error) {
    // Anzeige ist schon gekürzt, die DB nicht — das muss der Händler wissen, sonst hält er den
    // gekürzten Stand für gespeichert und wundert sich beim nächsten Öffnen.
    msgEl.textContent = `In Vario ist „${namen}" dem Kanal nicht mehr zugeordnet. Das Nachziehen der Auswahl ist fehlgeschlagen: ${error.message}`;
    msgEl.className = 'mp-msg err';
    return;
  }
  const wrap = card?.querySelector<HTMLElement>('[data-storage-multi]');
  if (wrap) {
    wrap.dataset['selectedIds'] = sync.ids.join(',');
    wrap.dataset['defaultId'] = sync.defaultId ?? '';
  }
  msgEl.textContent = sync.ids.length > 0
    ? `„${namen}" ist in Vario dem Verkaufskanal nicht mehr zugeordnet und wurde aus der Bestandsquelle entfernt.`
    : `„${namen}" ist in Vario dem Verkaufskanal nicht mehr zugeordnet. Es ist jetzt KEIN Lager gewählt — der Bestand kommt bis zur nächsten Auswahl aus allen Verkaufslagern zusammen.`;
  msgEl.className = sync.ids.length > 0 ? 'mp-msg info' : 'mp-msg err';
}

/** Icon-Button neben der Lager-Auswahl: Vario-Lager-Liste neu vom Edge-Endpoint holen
 *  und alle Lager-Panels (über alle Karten) neu rendern. */
async function reloadWarehouses(btn: HTMLButtonElement, ctx: SettingsCtx): Promise<void> {
  const card = btn.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-storage-msg]');
  const icon = btn.querySelector<HTMLElement>('[data-icon-default]');
  const originalIconClass = icon?.className ?? 'v-icon fa-regular fa-rotate-right';
  btn.disabled = true;
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
  try {
    // Der Button liegt AUSSERHALB von .mp-multi: derselbe Klick startet per Bubbling auch
    // den Dokument-Handler, der alle offenen Panels schliesst und dabei speichert. Loest
    // der Lager-Abruf zuerst auf, rendert applyWarehousesToMulti das Panel aus dem noch
    // alten dataset.selectedIds (das erst nach RPC-Erfolg nachgefuehrt wird) — die Summary
    // springt dann sichtbar auf den Vorzustand, obwohl daneben "N Lager gespeichert" steht.
    // Deshalb ALLE Panels abwarten, nicht nur das der eigenen Karte: der Dokument-Handler
    // schliesst ebenfalls alle. Fuer bereits geschlossene Panels ist closeStoragePanel ein
    // No-op (Fruehausstieg auf panel.hidden) → keine unnoetigen Speicher-Calls.
    await Promise.all(
      Array.from(document.querySelectorAll<HTMLElement>('[data-storage-multi]'))
        .map((w) => closeStoragePanel(w, ctx)),
    );
    const warehouses = await loadVarioWarehouses(ctx);
    cachedWarehouses = warehouses;
    const listEl = btn.closest<HTMLElement>('.kl-storefront-grid') ?? document;
    listEl.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      applyWarehousesToMulti(wrap, warehouses);
    });
    showToast(
      warehouses.length > 0 ? `${warehouses.length} Vario-Lager geladen.` : 'Keine Vario-Lager gefunden.',
      warehouses.length > 0 ? 'success' : 'info',
    );
    if (msgEl) { msgEl.textContent = ''; msgEl.className = 'mp-msg'; }
  } catch (e) {
    showToast(`Vario-Lager konnten nicht geladen werden: ${(e as Error).message}`, 'error');
  } finally {
    btn.disabled = false;
    if (icon) icon.className = originalIconClass;
  }
}

/** Klick außerhalb und Escape schließen offene Lager-Panels (und speichern dabei).
 *  Nur EINMAL binden — renderSettingsTab läuft bei jedem Refresh erneut, sonst stapeln
 *  sich die Listener und ein Klick löst N Speichervorgänge aus. */
let storageGlobalHandlersBound = false;
function bindStorageGlobalHandlers(ctx: SettingsCtx): void {
  if (storageGlobalHandlersBound) return;
  storageGlobalHandlersBound = true;
  document.addEventListener('click', (ev) => {
    const target = ev.target as Node | null;
    document.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      if (target && wrap.contains(target)) return;
      void closeStoragePanel(wrap, ctx);
    });
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    document.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      void closeStoragePanel(wrap, ctx);
    });
  });
}

async function toggleSuppressStock(cb: HTMLInputElement, ctx: SettingsCtx): Promise<void> {
  const storefront = cb.dataset.storefront!;
  const card = cb.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-suppress-msg]');
  const wantedValue = cb.checked;
  cb.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_suppress_stock_push', {
    _storefront: storefront,
    _suppress: wantedValue,
  });
  cb.disabled = false;
  if (msgEl) {
    if (error) {
      cb.checked = !wantedValue;
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = wantedValue
        ? 'Bestand-Push deaktiviert. Preis-/Status-Updates laufen weiter.'
        : 'Bestand-Push wieder aktiviert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

async function toggleAutoTakeover(cb: HTMLInputElement, ctx: SettingsCtx): Promise<void> {
  const storefront = cb.dataset.storefront!;
  const card = cb.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-auto-takeover-msg]');
  const wantedValue = cb.checked;
  cb.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_auto_takeover_orders', {
    _storefront: storefront,
    _enabled: wantedValue,
  });
  cb.disabled = false;
  if (msgEl) {
    if (error) {
      cb.checked = !wantedValue;
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = wantedValue
        ? 'Auto-Übernahme aktiviert. Neue Bestellungen werden ab dem nächsten Refresh automatisch angelegt.'
        : 'Auto-Übernahme deaktiviert. Bestellungen müssen manuell übernommen werden.';
      msgEl.className = 'mp-msg ok';
    }
  }

}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
